/**
 * Composition Tab - Portfolio allocation view
 * FOCUSED PURPOSE: Show allocation structure, identify drift, suggest rebalancing
 *
 * What it shows:
 * - Asset & Currency allocation (visual pie charts)
 * - Drift from target allocation (with rebalancing actions)
 * - Top holdings (name, value, weight, sector)
 * - Cash positions (multi-currency breakdown)
 * - Quick actions (proactive suggestions)
 *
 * What it doesn't show (moved to other tabs):
 * - Performance/P&L → Performance Tab
 * - Risk metrics → Risk Tab
 * - Transaction history → History Tab
 * - Deep security analysis → Research Tab
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Position as ApiPosition, AssetAllocation, CashPosition as ApiCashPosition } from '../../services/api/portfolioApiService';
import {
  AllocationBarChart,
  AllocationPieChart,
  AllocationTable,
  CashPositionCard,
  QuickActions
} from './index';
import type { AllocationData, AllocationRow, Position, CashPosition } from './index';
import type { QuickAction } from './molecules/QuickActions';
import { PortfolioFilters, AssetClassFilter as AssetClassFilterType } from '../../types/portfolio-views';
import { PieChart, BarChart3, HelpCircle } from 'lucide-react';
import { portfolioApiService } from '../../services/api/portfolioApiService';
import ExportDropdown from './atoms/ExportDropdown';
import { OpenAsSpreadsheet } from '../canvas/widgets/OpenAsSpreadsheet';
import RebalanceProposal from './RebalanceProposal';
import { exportToCSV } from '../../utils/portfolioExport';
import { usePortfolioPptxExport } from './hooks/usePortfolioPptxExport';

/** The canonical drift payload (TASK-723). Shape mirrors `portfolioApiService.getDrift`. */
// ponytail: a local copy of the shape `hooks/useCanonicalDrift.ts` already exports, and
// `ConsolidatedHoldings.tsx` declares a third. Collapsing them is its own change; this one only
// adds the field it needs, to the copy that needs it.
type CanonicalDrift = {
  assessment_status?: 'assessed' | 'incomplete';
  assessed_at?: string;
  calculation_version?: number;
  tolerance_pct?: number;
  needs_rebalancing: boolean | null;
  breached_slots?: string[] | null;
  message?: string;
  /**
   * TASK-863: the CHF total the percentages were computed against. `portfolioValue` is a par
   * sum of `market_value` in the custodian's currencies, so multiplying it by a converted
   * percentage is wrong in both units on a mixed-currency book.
   */
  total_portfolio_value?: number;
  drift_by_asset_class?: Record<string, {
    current: number;
    target: number;
    drift: number;
    breach?: boolean;
    asset_class?: string | null;
    sub_asset_class?: string | null;
  }>;
};

interface CompositionTabProps {
  positions: ApiPosition[];
  allocation: AssetAllocation | null;
  cashPositions: ApiCashPosition[];
  totalCashCHF?: number;
  portfolioValue?: number;
  loading?: boolean;
  hidePositions?: boolean;
  filters?: PortfolioFilters;
  clientId?: string;
  portfolioId?: string;
}

const CompositionTab: React.FC<CompositionTabProps> = ({
  positions,
  allocation,
  cashPositions,
  totalCashCHF,
  portfolioValue = 0,
  loading = false,
  hidePositions = false,
  filters,
  clientId,
  portfolioId,
}) => {
  // Chart type toggle state
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');

  // TASK-207: Helper function to map API asset class to filter type (defined BEFORE use)
  const mapAssetClassToFilter = (assetClass: string): AssetClassFilterType => {
    const lowerClass = assetClass.toLowerCase();
    if (lowerClass.includes('equity') || lowerClass.includes('stock')) return 'equities';
    if (lowerClass.includes('bond') || lowerClass.includes('fixed income')) return 'bonds';
    if (lowerClass.includes('fund') || lowerClass.includes('etf')) return 'funds';
    if (lowerClass.includes('derivative') || lowerClass.includes('option')) return 'derivatives';
    if (lowerClass.includes('cash') || lowerClass.includes('money market')) return 'cash';
    if (lowerClass.includes('alternative') || lowerClass.includes('real estate')) return 'alternative';
    return 'all';
  };

  // TASK-207: Apply client-side filters to positions
  const filteredPositions = useMemo(() => {
    if (!filters) return positions;

    return positions.filter((position) => {
      // Filter by asset class
      if (!filters.assetClasses.includes('all')) {
        const positionAssetClass = mapAssetClassToFilter(position.asset_class);
        if (!filters.assetClasses.includes(positionAssetClass)) {
          return false;
        }
      }

      // Filter by currency
      if (!filters.currencies.includes('all')) {
        if (!filters.currencies.includes(position.currency as any)) {
          return false;
        }
      }

      // Filter by account (if accounts are available in position data)
      // TODO: Uncomment when backend provides accountId in position data
      // if (filters.accounts.length > 0) {
      //   const accountEnabled = filters.accounts.find(acc => acc.id === position.accountId)?.enabled;
      //   if (!accountEnabled) {
      //     return false;
      //   }
      // }

      return true;
    });
  }, [positions, filters, mapAssetClassToFilter]);

  // TASK-207: Apply filters to cash positions
  const filteredCashPositions = useMemo(() => {
    if (!filters || filters.currencies.includes('all')) return cashPositions;

    return cashPositions.filter((cash) =>
      filters.currencies.includes(cash.currency as any)
    );
  }, [cashPositions, filters]);

  // Asset class colors.
  //
  // TASK-877: this map was keyed on `Equity` / `Bond` / `Cash` / `ETF`, none of which the
  // API emits. `by_asset_class` carries the canonical five from `shared/portfolio/taxonomy.py`
  // — measured live for Schneider on 2026-08-30:
  //
  //     {"Alternatives": 8.65, "Fixed Income": 31.95, "Liquidity": 2.9,
  //      "Equities": 51.63, "Real Estate": 4.88}
  //
  // Only `Real Estate` matched, so four of the five slices fell through to the `Other`
  // grey and the chart read as monochrome — while the geography pie beside it, which
  // colours by index rather than by name, had a full palette. Keyed on the canonical
  // names now, with the pre-taxonomy labels kept as aliases: this component also renders
  // uploaded portfolios, whose labels do not all go through `normalize_ac`.
  //
  // Grey is reserved for `Other`. An unclassified slice should look unclassified.
  const COLORS = {
    // Canonical — `ASSET_CLASSES` in shared/portfolio/taxonomy.py
    'Equities': '#002B45',
    'Fixed Income': '#F7B500',
    'Liquidity': '#28A745',
    'Alternatives': '#17A2B8',
    'Real Estate': '#DC3545',
    // Pre-taxonomy labels, mapped to the same colour as their canonical counterpart so a
    // legacy payload does not recolour the same slice.
    'Equity': '#002B45',
    'Bond': '#F7B500',
    'Cash': '#28A745',
    // `ETF` is a wrapper, not an asset class, so it has no canonical counterpart to
    // inherit from. Review caught the first cut parking it on the old Cash grey
    // (#6C757D), a second grey barely distinguishable from `Other` — which made a
    // CLASSIFIED bucket read as unclassified, breaking the rule stated directly above.
    // It gets a colour of its own.
    'ETF': '#6F42C1',
    'Other': '#9E9E9E',
  };

  // Prepare data for AllocationPieChart
  const assetClassData: AllocationData[] = allocation?.by_asset_class
    ? Object.entries(allocation.by_asset_class)
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({
          name,
          value,
          percentage: value,
          color: COLORS[name as keyof typeof COLORS] || COLORS['Other'],
        }))
    : [];

  const currencyData: AllocationData[] = allocation?.by_currency
    ? Object.entries(allocation.by_currency)
        .filter(([_, value]) => value > 0)
        .map(([name, value], index) => ({
          name,
          value,
          percentage: value,
          // Generate stable colors for currencies
          color: ['#002B45', '#F7B500', '#28A745', '#DC3545', '#6C757D', '#17A2B8'][index] || '#9E9E9E'
        }))
    : [];

  const geographyData: AllocationData[] = allocation?.by_geography
    ? Object.entries(allocation.by_geography)
        .filter(([_, value]) => value > 0)
        .map(([name, value], index) => ({
          name,
          value,
          percentage: value,
          color: ['#002B45', '#F7B500', '#28A745', '#DC3545', '#6C757D', '#17A2B8', '#E91E63', '#9C27B0'][index] || '#9E9E9E'
        }))
    : [];

  // TASK-874: the chart can show `Europe` next to `Germany`, and an advisor reading it has no
  // way to tell whether the first contains the second. It does not — every position lands in
  // exactly one bucket — but that is a property of the calculation, invisible on a pie. The
  // caption states it, and only when both tiers are actually on screen.
  //
  // Only tier `region` is captioned. An `unresolved` bucket is a label the backend vocabulary
  // could not place, which is very often a real country — review caught the first cut calling
  // a Greek holding "a region, not a country" in a panel an advisor shows a client.
  const geographyTiers = allocation?.by_geography_tier ?? {};
  const geographyRegionNames = geographyData
    .map(({ name }) => name)
    .filter((name) => geographyTiers[name] === 'region');
  const hasGeographyCountries = geographyData.some(
    ({ name }) => geographyTiers[name] === 'country',
  );

  // TASK-723: the canonical drift classification. Composition used to derive its OWN verdict
  // here — asset-class granularity against hardcoded 5% / 2% constants — which is why it
  // could say "optimal" about the very sub-asset class the Radar was alerting on. It now
  // reads GET /api/portfolio/drift/{clientId}, the same calculation that emits
  // `portfolio_drift_breach`, and renders whatever that says. No constants remain.
  // TASK-868: the slot the advisor asked to reduce. Null until they click `Decrease` — the
  // panel is a consequence of an intent, not something the page opens on its own.
  const [proposalSlot, setProposalSlot] = useState<string | null>(null);

  const [driftResult, setDriftResult] = useState<CanonicalDrift | null>(null);
  const [driftState, setDriftState] = useState<'loading' | 'ready' | 'incomplete' | 'failed'>(
    'loading',
  );
  const [driftNote, setDriftNote] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) {
      setDriftState('incomplete');
      setDriftNote('No client selected.');
      return;
    }
    let cancelled = false;
    setDriftState('loading');
    portfolioApiService
      .getDrift(clientId, portfolioId)
      .then((res) => {
        if (cancelled) return;
        const d = res.drift as CanonicalDrift;
        setDriftResult(d);
        setDriftState(d.assessment_status === 'incomplete' ? 'incomplete' : 'ready');
        setDriftNote(d.assessment_status === 'incomplete' ? (d.message ?? null) : null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        // The endpoint answers 422 `risk_profile_incomplete` when section7 is missing. That
        // is the correct answer, not a failure — and it must render as "cannot assess",
        // never as a verdict (TASK-722's pattern).
        const detail = err?.response?.data?.detail;
        const incomplete =
          err?.response?.status === 422 || detail?.error === 'risk_profile_incomplete';
        setDriftState(incomplete ? 'incomplete' : 'failed');
        setDriftNote(detail?.message ?? err?.message ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, portfolioId]);

  // Prepare data for AllocationTable — straight from the canonical per-slot classification.
  const allocationTableData: AllocationRow[] = useMemo(() => {
    const slots = driftResult?.drift_by_asset_class;
    if (!slots) return [];
    // TASK-863: multiply the percentages by the total THEY were computed against.
    // `portfolioValue` is a par sum of `market_value` in whatever currency the custodian
    // delivered; the slot percentages are CHF-converted. Multiplying one by the other is wrong
    // in both units on a mixed-currency book — it was self-consistent only while both were par.
    // `total_portfolio_value` comes from the same calculation as `slot.current`.
    const driftBase = driftResult?.total_portfolio_value ?? portfolioValue;
    return Object.entries(slots).map(([key, slot]) => ({
      name: key,
      category: slot.asset_class ?? key,
      current: slot.current,
      value: driftBase ? (driftBase * slot.current) / 100 : 0,
      percentage: slot.current,
      target: slot.target,
      drift: slot.drift,
      difference: slot.drift,
      // The canonical breach flag — the client's own tolerance band, decided server-side.
      status: slot.breach ? ('critical' as const) : ('optimal' as const),
      action:
        !slot.breach
          ? ('maintain' as const)
          : slot.drift > 0
            ? ('decrease' as const)
            : ('increase' as const),
    })) as AllocationRow[];
  }, [driftResult, portfolioValue]);

  // Transform positions data for PositionsList (use filtered positions)
  const positionsListData: Position[] = filteredPositions.map(p => ({
    isin: p.isin,
    name: p.security_name,
    quantity: p.quantity || 0,
    price: p.current_price || 0,
    value: p.market_value || 0,
    cost_basis: p.book_value,
    unrealized_pl: p.unrealized_pl,
    unrealized_pl_pct: p.book_value > 0 ? (p.unrealized_pl / p.book_value * 100) : 0,
    asset_class: p.asset_class,
    currency: p.currency,
    // TASK-865 review: was `p.market_value / portfolioValue`. `market_value` is the
    // custodian's LOCAL figure and `portfolioValue` is CHF-converted, so a EUR position's
    // weight was overstated by 1/0.96. `market_value_base` is the server-converted value.
    weight:
      portfolioValue > 0 && p.market_value_base != null
        ? (p.market_value_base / portfolioValue) * 100
        : 0
  }));

  // Generate Quick Actions based on portfolio state
  const quickActions: QuickAction[] = useMemo(() => {
    const actions: QuickAction[] = [];

    // Check for rebalancing needs
    // TASK-723: 'critical' here IS the canonical breach flag — the client's own tolerance
    // band, decided server-side. There is no local threshold left to disagree with it, and
    // no separate 'warning' tier: a slot is either outside the client's band or inside it.
    const criticalDrift =
      driftState === 'ready' ? allocationTableData.find(row => row.status === 'critical') : undefined;

    if (criticalDrift) {
      // TASK-863: same base as the percentages it multiplies (see `driftBase` above).
      const driftBaseForCopy = driftResult?.total_portfolio_value ?? portfolioValue;
      const driftAmount = driftBaseForCopy * Math.abs(criticalDrift.drift ?? 0) / 100;
      actions.push({
        type: 'rebalance',
        title: 'Rebalancing Needed',
        description: `${criticalDrift.name} is ${Math.abs(criticalDrift.drift ?? 0).toFixed(1)}% ${(criticalDrift.drift ?? 0) > 0 ? 'above' : 'below'} target. ${criticalDrift.action === 'decrease' ? 'Reduce' : 'Add'} approximately CHF ${driftAmount.toLocaleString('de-CH', { maximumFractionDigits: 0 })}.`,
        actionLabel: 'Generate trade list'
      });
    }

    // Check for upcoming dividends (mock data - would come from API)
    const hasUpcomingDividends = positions.some(p => p.expected_dividend_date);
    if (hasUpcomingDividends) {
      actions.push({
        type: 'income',
        title: 'Upcoming Dividend Income',
        description: `Expected dividend payments in the next 30 days.`,
        actionLabel: 'View income calendar'
      });
    }

    // All optimal - success message
    if (actions.length === 0 && allocationTableData.length > 0) {
      actions.push({
        type: 'success',
        title: 'Portfolio Well Balanced',
        description: 'All asset classes are within target ranges. No immediate action required.'
      });
    }

    return actions.slice(0, 3); // Limit to 3 actions max
  }, [allocationTableData, portfolioValue, positions]);

  // Export handler — PPTX flows through the LLM pipeline, CSV stays local.
  const { loading: exportLoading, runExport } = usePortfolioPptxExport({
    clientId,
    portfolioId,
    onCsv: () => exportToCSV(filteredPositions, allocation, filteredCashPositions, portfolioValue),
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chart Type Toggle & Export */}
      <div className="flex justify-between items-center">
        {/* Chart Type Toggle */}
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
          <button
            onClick={() => setChartType('pie')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              chartType === 'pie'
                ? 'bg-kielis-navy text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <PieChart className="w-4 h-4" />
            Pie Chart
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              chartType === 'bar'
                ? 'bg-kielis-navy text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Bar Chart
          </button>
        </div>

        {/* Export Dropdown */}
        <ExportDropdown
          onExport={runExport}
          disabled={loading || positionsListData.length === 0}
          loading={exportLoading}
        />
      </div>

      {/* Asset Allocation Charts */}
      <div className={`grid grid-cols-1 ${geographyData.length > 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-6`}>
        {/* Asset Class Allocation */}
        <div className="bg-white rounded-lg shadow p-6">
          {assetClassData.length > 0 ? (
            chartType === 'pie' ? (
              <AllocationPieChart
                data={assetClassData}
                title="Asset Class Allocation"
                showLegend={true}
                showTooltip={true}
                height={350}
              />
            ) : (
              <AllocationBarChart
                data={assetClassData}
                title="Asset Class Allocation"
                showLegend={false}
                showTooltip={true}
                height={350}
              />
            )
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
              No allocation data available
            </div>
          )}
        </div>

        {/* Currency Allocation */}
        <div className="bg-white rounded-lg shadow p-6">
          {currencyData.length > 0 ? (
            chartType === 'pie' ? (
              <AllocationPieChart
                data={currencyData}
                title="Currency Allocation"
                showLegend={true}
                showTooltip={true}
                height={350}
              />
            ) : (
              <AllocationBarChart
                data={currencyData}
                title="Currency Allocation"
                showLegend={false}
                showTooltip={true}
                height={350}
              />
            )
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
              No currency data available
            </div>
          )}
        </div>

        {/* Geographic Allocation */}
        {geographyData.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            {chartType === 'pie' ? (
              <AllocationPieChart
                data={geographyData}
                title="Geographic Allocation"
                showLegend={true}
                showTooltip={true}
                height={350}
              />
            ) : (
              <AllocationBarChart
                data={geographyData}
                title="Geographic Allocation"
                showLegend={false}
                showTooltip={true}
                height={350}
              />
            )}
            {hasGeographyCountries && geographyRegionNames.length > 0 && (
              <p className="mt-3 text-xs text-gray-500">
                {geographyRegionNames.join(', ')}{' '}
                {geographyRegionNames.length === 1 ? 'is a region' : 'are regions'}, not a
                country. Regions hold only the holdings with no country-level classification,
                so they do not include the countries listed separately.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Target vs Actual Allocation Table — TASK-723: one classification, stated honestly.
          An assessment that could not be made renders as exactly that, never as "optimal". */}
      {driftState === 'loading' && (
        <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-500">
          Assessing allocation drift…
        </div>
      )}
      {(driftState === 'incomplete' || driftState === 'failed') && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900">
                {driftState === 'incomplete'
                  ? 'Allocation drift cannot be assessed'
                  : 'Allocation drift assessment failed'}
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                {driftNote ||
                  (driftState === 'incomplete'
                    ? 'This client has no target allocation on file, so there is nothing to measure drift against.'
                    : 'The assessment could not be completed. This says nothing about the allocation itself.')}
              </p>
              {driftState === 'incomplete' && (
                <p className="text-xs text-gray-500 mt-2">
                  Complete the risk profiling questionnaire (section 7) to enable this.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {driftState === 'ready' && allocationTableData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          {/* TASK-876: the breach table is the third handover artefact the ticket names.
              Sits above the table rather than in a toolbar because this molecule takes
              no toolbar prop and giving it one would change every other caller. */}
          <div className="mb-3 flex justify-end">
            <OpenAsSpreadsheet
              sourceWidget="AllocationTable"
              clientId={clientId}
              // No `asOf` passed: `CanonicalDrift` carries no calculation timestamp, so
              // the read clock is the most this surface can honestly claim. DriftBars
              // passes its real one because `useDrift` exposes it. Guessing a data
              // timestamp here would be worse than recording when it was read.
              build={() => ({
                title: 'Allocation vs target',
                sheetName: 'Allocation',
                columns: [
                  { header: 'Sub-asset class', width: 28 },
                  { header: 'Asset class', width: 20 },
                  { header: 'Current', fmt: 'percent', width: 12 },
                  { header: 'Target', fmt: 'percent', width: 12 },
                  // Percentage POINTS — a plain number, deliberately not `percent`.
                  { header: 'Drift pp', width: 12 },
                  { header: 'Value CHF', fmt: 'currency', width: 18, total: 'sum' },
                  { header: 'Outside band', width: 14 },
                ],
                // Excel's `0.0%` multiplies the cell by 100 to display it, and these are
                // already 0-100 — `value: driftBase * slot.current / 100` above is the
                // proof of scale. Written raw, a 43% slot printed as `4300.0%` on the
                // sheet an advisor hands to a colleague.
                rows: allocationTableData.map((r) => [
                  r.name ?? '',
                  r.category ?? '',
                  r.percentage == null ? null : r.percentage / 100,
                  r.target == null ? null : r.target / 100,
                  r.drift ?? null,
                  r.value ?? null,
                  r.status === 'critical' ? 'yes' : 'no',
                ]),
              })}
            />
          </div>
          <AllocationTable
            data={allocationTableData}
            title="Allocation vs Target"
            showTarget={true}
            showDrift={true}
            showValue={true}
            showActions={true}
            showStatus={true}
            currency="CHF"
            // TASK-868: `Decrease` was a styled button with no handler, on the row this table
            // had just labelled CRITICAL. It now opens a sized proposal for that slot, drawn
            // from the positions the client actually holds in it.
            onActionClick={
              clientId
                ? (row, action) => {
                    // Only `decrease` opens a proposal. The handler used to ignore its second
                    // argument, so any action that reached it opened one — including the
                    // labels that are not actions at all.
                    if (action === 'decrease') setProposalSlot(row.name || row.category);
                  }
                : undefined
            }
          />
          {/* The provenance of the verdict, so an RM comparing this with the Radar can tell
              whether they are looking at the same calculation (TASK-723 AC). */}
          <p className="text-xs text-gray-500 mt-3">
            Assessed against this client&apos;s own ±{driftResult?.tolerance_pct ?? '—'}pp
            tolerance at sub-asset-class level
            {driftResult?.assessed_at
              ? ` · ${new Date(driftResult.assessed_at).toLocaleString('de-CH')}`
              : ''}
            {driftResult?.calculation_version
              ? ` · calculation v${driftResult.calculation_version}`
              : ''}
          </p>
        </div>
      )}

      {/* TASK-868: the primitive the five detecting surfaces never had. Rendered directly under
          the table the advisor clicked, so the breach and what fixing it costs are on one
          screen. */}
      {proposalSlot && clientId && (
        <RebalanceProposal
          clientId={clientId}
          slot={proposalSlot}
          portfolioId={portfolioId}
          onClose={() => setProposalSlot(null)}
        />
      )}

      {/* Quick Actions - Context-aware suggestions */}
      {quickActions.length > 0 && <QuickActions actions={quickActions} />}

      {/* Positions Table - Only render if not hidden */}
      {!hidePositions && filteredPositions.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Holdings</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Security
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Weight
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPositions.map((p, index) => {
                  // TASK-865 review: converted numerator over a converted denominator.
                  const weight =
                    portfolioValue > 0 && p.market_value_base != null
                      ? (p.market_value_base / portfolioValue) * 100
                      : 0;
                  const industryParts = [p.sector, p.industry_group, p.industry].filter(Boolean);
                  return (
                    <tr key={p.isin || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{p.security_name}</p>
                          <p className="text-xs text-gray-500">{p.isin}</p>
                          {industryParts.length > 0 && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {industryParts.join(' > ')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {p.quantity ? p.quantity.toLocaleString('de-CH') : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div>
                          <p className="text-sm text-gray-900">
                            {p.current_price
                              ? new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(p.current_price)
                              : '-'}
                          </p>
                          {p.fx_rate_local_to_base && p.fx_rate_local_to_base !== 1.0 && (
                            <p className="text-xs text-gray-400 mt-0.5">FX {p.fx_rate_local_to_base.toFixed(4)}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(p.market_value)}
                          </p>
                          {p.market_value_local_ccy && p.market_value_local_ccy !== p.currency && p.market_value_local != null && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Local: {p.market_value_local_ccy} {p.market_value_local.toLocaleString('de-CH', { maximumFractionDigits: 0 })}
                            </p>
                          )}
                          {p.accrued_interest_base != null && p.accrued_interest_base !== 0 && (
                            <p className="text-xs text-blue-500 mt-0.5">
                              Accrued: {p.accrued_interest_base_ccy || 'CHF'} {p.accrued_interest_base.toLocaleString('de-CH', { maximumFractionDigits: 0 })}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {weight ? `${weight.toFixed(2)}%` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cash Positions */}
      {filteredCashPositions.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cash Positions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCashPositions.map((cash, index) => {
              // TASK-865: `amount_chf` never existed, so `exchange_rate` computed
              // `(undefined ?? 0) / amount` = 0 and rendered "0" for every non-CHF row, and
              // the percentage was 0 too. `amount_base` is converted server-side through the
              // one FX table; null means the platform has no rate, which is shown as such
              // rather than as a rate of zero.
              const amountBase = cash.amount_base ?? null;
              const cashData: CashPosition = {
                currency: cash.currency,
                amount: cash.amount,
                amount_base: amountBase,
                base_currency: cash.base_currency,
                exchange_rate:
                  amountBase !== null && cash.amount > 0 ? amountBase / cash.amount : undefined,
                percentage:
                  amountBase !== null && portfolioValue > 0
                    ? (amountBase / portfolioValue) * 100
                    : 0
              };
              return (
                <div key={index}>
                  <CashPositionCard
                    position={cashData}
                    baseCurrency="CHF"
                    showExchangeRate={true}
                    showPercentage={true}
                  />
                  {(cash.account_name || cash.account_number) && (
                    <p className="text-xs text-gray-400 mt-1 px-4">
                      {[cash.account_name, cash.account_number].filter(Boolean).join(' - ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {totalCashCHF !== undefined && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Total Cash (CHF)</span>
                <span className="text-lg font-bold text-gray-900">
                  CHF {totalCashCHF.toLocaleString('de-CH')}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompositionTab;